import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSearch,
  faTags,
  faCheck,
  faPlus,
  faSpinner,
  faTicketAlt,
  faUserTie,
  faClock,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { getStatusBadge, getIncidentTimeInfo } from '../../../settings/tags/tagStatusHelper';

/**
 * Modal para asignar una incidencia al ticket actual
 * Cumple con el requerimiento:
 * - Filtra por default las incidencias "En Progreso"
 * - Permite conmutar a la pestaña "Todas"
 * - Permite buscar en tiempo real por título o por ID (#INC-125 o 125)
 */
const TagAssignTicketModal = ({
  isOpen,
  onClose,
  ticketId,
  assignedTags = [],
  onAssignTag,
  isSavingTag = false,
  token,
  isDark = false
}) => {
  const [activeTab, setActiveTab] = useState('en_progreso'); // 'en_progreso' | 'todas'
  const [searchTerm, setSearchTerm] = useState('');
  const [catalogTags, setCatalogTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const prevSearchTermRef = useRef('');

  // Cargar catálogo de incidencias desde la API (con soporte de búsqueda directa a la BD)
  const fetchTags = useCallback(async (query = '') => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      const clean = (query || '').trim();
      if (clean) params.append('searchTerm', clean);

      const queryString = params.toString() ? `?${params.toString()}` : '';
      const resp = await fetch(`/api/SupportEngine/GetEtiquetas${queryString}`, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });
      if (resp.ok) {
        const result = await resp.json();
        const list = result.data || (Array.isArray(result) ? result : []);
        setCatalogTags(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.error('Error al cargar catálogo de incidencias:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Cargar catálogo al abrir el modal
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      prevSearchTermRef.current = '';
      setActiveTab('en_progreso');
      fetchTags('');
    }
  }, [isOpen, fetchTags]);

  // Búsqueda con debounce en la BD solo cuando el usuario cambia el término de búsqueda
  useEffect(() => {
    if (!isOpen) return;

    // Evitar disparo duplicado en la apertura inicial cuando el término no ha cambiado
    if (prevSearchTermRef.current === searchTerm) return;
    prevSearchTermRef.current = searchTerm;

    const timer = setTimeout(() => {
      fetchTags(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, isOpen, fetchTags]);

  // Filtrado reactivo: al buscar busca entre TODAS las incidencias por título o ID; sin búsqueda respeta la pestaña
  const filteredTags = useMemo(() => {
    let result = catalogTags;

    const cleanSearch = searchTerm.trim().toLowerCase();
    const cleanNoHash = cleanSearch.replace(/^#/, '').trim();
    const cleanNum = cleanNoHash.replace(/^inc-/, '').trim();

    // Si NO hay búsqueda, respeta la pestaña seleccionada ('en_progreso' o 'todas')
    // Si HAY búsqueda, busca entre TODAS las incidencias para permitir asignar cualquiera por ID o título
    if (!cleanSearch && activeTab === 'en_progreso') {
      result = result.filter(tag => Number(tag.iEstadoIncidencia) === 2);
    }

    // Filtrado reactivo en memoria para respuesta inmediata
    if (cleanSearch) {
      result = result.filter(tag => {
        const idIncStr = `inc-${tag.idEtiqueta}`.toLowerCase();
        const idHashIncStr = `#inc-${tag.idEtiqueta}`.toLowerCase();
        const idNumStr = String(tag.idEtiqueta);
        const nameStr = (tag.vNombre || '').toLowerCase();
        const descStr = (tag.vDescripcion || '').toLowerCase();
        const respStr = (tag.vResponsable || '').toLowerCase();

        return (
          nameStr.includes(cleanSearch) ||
          idIncStr.includes(cleanSearch) ||
          idHashIncStr.includes(cleanSearch) ||
          idNumStr === cleanSearch ||
          idNumStr === cleanNoHash ||
          idNumStr === cleanNum ||
          descStr.includes(cleanSearch) ||
          respStr.includes(cleanSearch)
        );
      });
    }

    return result;
  }, [catalogTags, activeTab, searchTerm]);

  // Contadores para pestañas
  const countEnProgreso = useMemo(
    () => catalogTags.filter(t => Number(t.iEstadoIncidencia) === 2).length,
    [catalogTags]
  );
  const countTodas = catalogTags.length;

  if (!isOpen) return null;

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', zIndex: 1060 }}
      tabIndex="-1"
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: '640px', width: '95%' }}
        onClick={e => e.stopPropagation()}
      >
        <div
          className="modal-content border-0 shadow-2xl"
          style={{
            backgroundColor: isDark ? '#141e2c' : '#ffffff',
            borderRadius: '20px',
            overflow: 'hidden'
          }}
        >
          {/* Header del Modal */}
          <div
            className="p-4 pb-3 d-flex align-items-center justify-content-between border-bottom"
            style={{ borderColor: isDark ? '#223247' : '#f1f5f9' }}
          >
            <div className="d-flex align-items-center gap-2.5">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: '38px',
                  height: '38px',
                  backgroundColor: isDark ? 'rgba(99, 102, 241, 0.2)' : '#e0e7ff',
                  color: '#6366f1'
                }}
              >
                <FontAwesomeIcon icon={faTags} style={{ fontSize: '1rem' }} />
              </div>
              <div>
                <h5
                  className="fw-bold mb-0"
                  style={{
                    color: isDark ? '#f8fafc' : '#0f172a',
                    fontSize: '1.15rem',
                    letterSpacing: '-0.3px'
                  }}
                >
                  Asignar Incidencia al Ticket #{ticketId}
                </h5>
                <span className="small text-muted" style={{ fontSize: '0.78rem' }}>
                  Vincula este ticket a una incidencia técnica abierta o en curso.
                </span>
              </div>
            </div>

            <button
              type="button"
              className="btn btn-sm p-1 rounded-circle border-0 text-muted hover-opacity shadow-none"
              onClick={onClose}
              style={{ fontSize: '1rem', width: '30px', height: '30px' }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Filtros: Pestañas + Barra de Búsqueda */}
          <div
            className="p-3 px-4 border-bottom d-flex flex-column"
            style={{
              backgroundColor: isDark ? '#182536' : '#f8fafd',
              borderColor: isDark ? '#223247' : '#f1f5f9',
              gap: '12px'
            }}
          >
            {/* Pestañas (En Progreso vs Todas) */}
            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
              <button
                type="button"
                className={`btn btn-sm rounded-pill fw-semibold shadow-none border d-inline-flex align-items-center`}
                style={{
                  backgroundColor:
                    activeTab === 'en_progreso'
                      ? '#4f46e5'
                      : isDark
                        ? '#1a2636'
                        : '#ffffff',
                  borderColor:
                    activeTab === 'en_progreso'
                      ? '#4f46e5'
                      : isDark
                        ? '#2a3b50'
                        : '#cbd5e1',
                  color:
                    activeTab === 'en_progreso'
                      ? '#ffffff'
                      : isDark
                        ? '#cbd5e1'
                        : '#475569',
                  fontSize: '0.78rem',
                  padding: '6px 14px',
                  gap: '8px',
                  cursor: 'pointer'
                }}
                onClick={() => setActiveTab('en_progreso')}
              >
                <span
                  style={{
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    backgroundColor: activeTab === 'en_progreso' ? '#ffffff' : '#6366f1',
                    flexShrink: 0
                  }}
                />
                <span>En Progreso</span>
                <span
                  className="badge rounded-pill"
                  style={{
                    backgroundColor:
                      activeTab === 'en_progreso'
                        ? 'rgba(255,255,255,0.25)'
                        : isDark
                          ? '#223247'
                          : '#e2e8f0',
                    color:
                      activeTab === 'en_progreso'
                        ? '#ffffff'
                        : isDark
                          ? '#e2e8f0'
                          : '#475569',
                    fontSize: '0.70rem',
                    padding: '3px 8px',
                    marginLeft: '4px'
                  }}
                >
                  {countEnProgreso}
                </span>
              </button>

              <button
                type="button"
                className={`btn btn-sm rounded-pill fw-semibold shadow-none border d-inline-flex align-items-center`}
                style={{
                  backgroundColor:
                    activeTab === 'todas'
                      ? '#4f46e5'
                      : isDark
                        ? '#1a2636'
                        : '#ffffff',
                  borderColor:
                    activeTab === 'todas'
                      ? '#4f46e5'
                      : isDark
                        ? '#2a3b50'
                        : '#cbd5e1',
                  color:
                    activeTab === 'todas'
                      ? '#ffffff'
                      : isDark
                        ? '#cbd5e1'
                        : '#475569',
                  fontSize: '0.78rem',
                  padding: '6px 14px',
                  gap: '8px',
                  cursor: 'pointer'
                }}
                onClick={() => setActiveTab('todas')}
              >
                <span>Todas</span>
                <span
                  className="badge rounded-pill"
                  style={{
                    backgroundColor:
                      activeTab === 'todas'
                        ? 'rgba(255,255,255,0.25)'
                        : isDark
                          ? '#223247'
                          : '#e2e8f0',
                    color:
                      activeTab === 'todas'
                        ? '#ffffff'
                        : isDark
                          ? '#e2e8f0'
                          : '#475569',
                    fontSize: '0.70rem',
                    padding: '3px 8px',
                    marginLeft: '4px'
                  }}
                >
                  {countTodas}
                </span>
              </button>
            </div>

            {/* Barra de Búsqueda */}
            <div
              className="d-flex align-items-center rounded-pill border"
              style={{
                backgroundColor: isDark ? '#141e2c' : '#ffffff',
                borderColor: isDark ? '#293a4f' : '#cbd5e1',
                padding: '8px 14px',
                gap: '10px'
              }}
            >
              <FontAwesomeIcon icon={faSearch} className="text-muted" style={{ fontSize: '0.85rem', flexShrink: 0 }} />
              <input
                type="text"
                className="border-0 bg-transparent shadow-none w-100 p-0"
                style={{
                  fontSize: '0.84rem',
                  outline: 'none',
                  color: isDark ? '#f8fafc' : '#0f172a'
                }}
                placeholder="Buscar por título, responsable o ID (#INC-125)..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                autoFocus
              />
              {searchTerm && (
                <button
                  type="button"
                  className="btn btn-sm p-0 text-muted border-0 bg-transparent"
                  onClick={() => setSearchTerm('')}
                >
                  <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.75rem' }} />
                </button>
              )}
            </div>
          </div>

          {/* Listado de Incidencias */}
          <div
            className="modal-body p-4 pt-3 d-flex flex-column gap-2.5"
            style={{ maxHeight: '55vh', overflowY: 'auto' }}
          >
            {isLoading ? (
              <div className="py-5 text-center text-muted">
                <FontAwesomeIcon icon={faSpinner} spin className="text-primary mb-2" style={{ fontSize: '1.4rem' }} />
                <div className="small">Cargando catálogo de incidencias...</div>
              </div>
            ) : filteredTags.length === 0 ? (
              <div
                className="text-center py-5 rounded-3 border border-dashed"
                style={{ borderColor: isDark ? '#293a4f' : '#cbd5e1' }}
              >
                <FontAwesomeIcon icon={faTags} className="text-muted mb-2" style={{ fontSize: '1.8rem', opacity: 0.4 }} />
                <h6 className="fw-semibold mb-1" style={{ color: isDark ? '#f8fafc' : '#0f172a' }}>
                  No se encontraron incidencias
                </h6>
                <p className="small text-muted mb-0" style={{ maxWidth: '380px', margin: '0 auto', fontSize: '0.80rem' }}>
                  {searchTerm
                    ? `No hay incidencias que coincidan con "${searchTerm}".`
                    : activeTab === 'en_progreso'
                      ? 'No hay incidencias marcadas como "En Progreso" actualmente. Cambia a la pestaña "Todas" para explorar el catálogo completo.'
                      : 'Aún no se han registrado incidencias en el sistema.'}
                </p>
              </div>
            ) : (
              filteredTags.map(tag => {
                const isAlreadyAssigned = assignedTags.some(
                  t => Number(t.idEtiqueta) === Number(tag.idEtiqueta)
                );
                const status = getStatusBadge(tag.iEstadoIncidencia, isDark);
                const timeInfo = getIncidentTimeInfo(tag);

                return (
                  <div
                    key={tag.idEtiqueta}
                    className="p-3 rounded-3 border d-flex align-items-center justify-content-between transition-all"
                    style={{
                      backgroundColor: isDark ? '#1a2636' : '#f8fafd',
                      borderColor: isAlreadyAssigned
                        ? isDark
                          ? '#1e3a5f'
                          : '#c7d2fe'
                        : isDark
                          ? '#293a4f'
                          : '#e2e8f0',
                      opacity: isAlreadyAssigned ? 0.75 : 1
                    }}
                  >
                    <div className="overflow-hidden pe-3 flex-grow-1">
                      <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px', marginBottom: '6px' }}>
                        {/* Badge #INC-125 */}
                        <span
                          className="badge rounded-pill fw-bold"
                          style={{
                            backgroundColor: isDark ? '#1e2c3d' : '#e0e7ff',
                            color: isDark ? '#818cf8' : '#4338ca',
                            fontSize: '0.74rem',
                            padding: '4px 8px'
                          }}
                        >
                          #INC-{tag.idEtiqueta}
                        </span>

                        {/* Estado */}
                        <span
                          className="badge rounded-pill fw-semibold d-inline-flex align-items-center"
                          style={{
                            backgroundColor: status.bg,
                            color: status.color,
                            border: `1px solid ${status.border}`,
                            fontSize: '0.70rem',
                            padding: '4px 8px',
                            gap: '6px'
                          }}
                        >
                          <span
                            style={{
                              width: '5px',
                              height: '5px',
                              borderRadius: '50%',
                              backgroundColor: status.color,
                              flexShrink: 0
                            }}
                          />
                          <span>{status.label}</span>
                        </span>

                        {/* Tiempo de la incidencia */}
                        <span
                          className="small d-inline-flex align-items-center"
                          style={{
                            fontSize: '0.72rem',
                            color: timeInfo.isResolved
                              ? (isDark ? '#34d399' : '#059669')
                              : (isDark ? '#94a3b8' : '#64748b'),
                            gap: '6px'
                          }}
                        >
                          <FontAwesomeIcon
                            icon={timeInfo.isResolved ? faCheckCircle : faClock}
                            style={{
                              fontSize: '0.68rem',
                              color: timeInfo.isResolved ? '#10b981' : '#6366f1'
                            }}
                          />
                          <span>{timeInfo.label}</span>
                        </span>
                      </div>

                      {/* Título de la incidencia */}
                      <h6
                        className="fw-bold text-truncate"
                        style={{
                          color: isDark ? '#f8fafc' : '#0f172a',
                          fontSize: '0.90rem',
                          marginBottom: '6px'
                        }}
                        title={tag.vNombre}
                      >
                        {tag.vNombre}
                      </h6>

                      {/* Metadata: Responsable y tickets asociados */}
                      <div
                        className="small d-flex align-items-center flex-wrap text-muted"
                        style={{ fontSize: '0.75rem', gap: '8px' }}
                      >
                        {tag.vResponsable ? (
                          <span className="d-inline-flex align-items-center" style={{ gap: '6px' }}>
                            <FontAwesomeIcon icon={faUserTie} style={{ color: '#6366f1' }} />
                            <span>{tag.vResponsable}</span>
                          </span>
                        ) : (
                          <span>Sin responsable asignado</span>
                        )}
                        <span className="opacity-50">•</span>
                        <span className="d-inline-flex align-items-center" style={{ gap: '6px' }}>
                          <FontAwesomeIcon icon={faTicketAlt} style={{ color: '#0ea5e9' }} />
                          <span>{tag.iTotalTickets || 0} tickets asociados</span>
                        </span>
                      </div>
                    </div>

                    {/* Botón de Asignar */}
                    <div className="flex-shrink-0">
                      {isAlreadyAssigned ? (
                        <span
                          className="badge rounded-pill fw-semibold d-inline-flex align-items-center"
                          style={{
                            backgroundColor: isDark ? '#1e2c3d' : '#e0e7ff',
                            color: isDark ? '#818cf8' : '#4338ca',
                            fontSize: '0.76rem',
                            padding: '6px 12px',
                            gap: '6px'
                          }}
                        >
                          <FontAwesomeIcon icon={faCheck} />
                          <span>Asignada</span>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm rounded-pill px-3 py-1.5 fw-semibold d-inline-flex align-items-center text-white shadow-none"
                          style={{
                            backgroundColor: '#4f46e5',
                            borderColor: '#4f46e5',
                            fontSize: '0.78rem',
                            gap: '6px'
                          }}
                          disabled={isSavingTag}
                          onClick={() => onAssignTag(tag.idEtiqueta)}
                        >
                          {isSavingTag ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            <FontAwesomeIcon icon={faPlus} />
                          )}
                          <span>Asignar</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer del Modal */}
          <div
            className="modal-footer border-top px-4 py-2.5 d-flex align-items-center justify-content-end"
            style={{ borderColor: isDark ? '#223247' : '#f1f5f9' }}
          >
            <button
              type="button"
              className="btn btn-sm btn-link text-muted text-decoration-none fw-semibold px-3 shadow-none"
              onClick={onClose}
              style={{ fontSize: '0.85rem' }}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagAssignTicketModal;
